import { computed, type ComputedRef } from 'vue'

import { useInsightActionSelection } from './useInsightActionSelection'

import { SELECTION_CEILING, hiddenSelectedCount } from '../service/insightActionSelection'
import { useInsightActionStore } from '../stores/insightActionStore'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'

export interface UseStepFooterCopy {
  headline: ComputedRef<string>
  coverage: ComputedRef<string>
  note: ComputedRef<string>
}

export function useStepFooterCopy(): UseStepFooterCopy {
  const { t } = useI18n()
  const format = useFormat()
  const store = useInsightActionStore()
  const selection = useInsightActionSelection()

  const headline = computed(() => {
    if (store.mode === 'history') {
      return t('insightActions.step.historyHeadline', {
        runs: store.history.length,
        changes: store.history.reduce((sum, run) => sum + run.totalItems, 0),
        count: store.history.length,
      })
    }
    if (store.step === 'select') {
      return t('insightActions.step.selectedOf', {
        selected: store.selectedCount,
        total: selection.pickable.value.length,
      })
    }
    if (store.step === 'confirm') {
      return t('insightActions.step.toSend', { count: store.selectedCount })
    }
    const total = store.run?.totalItems ?? 0
    if (store.step === 'progress') {
      return t('insightActions.step.applyingOf', {
        done: store.runProgress?.done ?? total,
        total,
      })
    }
    return store.isRunTerminal && store.run?.status === 'completed'
      ? t('insightActions.step.resultDone')
      : t('insightActions.step.resultPartly')
  })

  const coverage = computed(() => {
    if (store.mode === 'history') return ''
    if (store.step === 'progress' || store.step === 'result') return ''
    if (!selection.coverage.value) return ''
    return t('insightActions.step.coverage', {
      amount: format.formatCurrency(selection.coverage.value),
    })
  })

  const note = computed(() => {
    if (store.mode !== 'act' || store.step !== 'select') return ''
    if (store.executionEnabled === false) return t('insightActions.step.shadowNote')

    const recovery = store.staleRecovery
    if (recovery) {
      return recovery.dropped
        ? t('insightActions.step.staleKeptSome', {
            kept: recovery.kept,
            dropped: recovery.dropped,
            count: recovery.dropped,
          })
        : t('insightActions.step.staleKeptAll')
    }

    if (
      store.selectedCount === SELECTION_CEILING &&
      selection.pickable.value.length > SELECTION_CEILING
    ) {
      return t('insightActions.step.ceilingNote', { limit: SELECTION_CEILING })
    }

    const hidden = hiddenSelectedCount(selection.rows.value, store.selection)
    if (hidden) {
      return t('insightActions.step.hiddenSelected', {
        hidden,
        selected: store.selectedCount,
      })
    }

    if (!selection.pickable.value.length) return t('insightActions.step.nothingApplicable')
    return ''
  })

  return { headline, coverage, note }
}
